import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LayoutDashboard, Package, ShoppingBag, MapPin, ListChecks, Building2, Plug, Boxes, LogOut, Lock, Store, Scale, Users, Ticket, MessageSquareQuote, FileText, Image, FolderOpen, ShieldCheck, ClipboardCheck, Wrench, KeyRound } from 'lucide-react';
import AdminDashboard from './AdminDashboard';
import AdminProductos from './AdminProductos';
import AdminPedidos from './AdminPedidos';
import AdminSeoLocal from './AdminSeoLocal';
import AdminListados from './AdminListados';
import AdminLegal from './AdminLegal';
import AdminCupones from './AdminCupones';
import AdminOpiniones from './AdminOpiniones';
import AdminPlantillas from './AdminPlantillas';
import AdminContenido from './AdminContenido';
import AdminMedia from './AdminMedia';
import AdminSistema from './AdminSistema';
import AdminConsentimientos from './AdminConsentimientos';
import AdminAdmisiones from './AdminAdmisiones';
import AdminMantenimiento from './AdminMantenimiento';
import AdminSeguridad from './AdminSeguridad';
import PinModal from '../../components/PinModal';
import { RecaptchaV2, executeV3 } from '../../components/RecaptchaWidget';
import { api, LOGO_URL } from '../../lib/api';
import { getToken, setToken, clearToken, clearElevated, formatApiError } from '../../lib/auth';
import { toast } from 'sonner';

const SECTIONS = [
  { id: 'dashboard', label: 'Escritorio', icon: LayoutDashboard },
  { id: 'productos', label: 'Productos', icon: Package },
  { id: 'pedidos', label: 'Pedidos', icon: ShoppingBag },
  { id: 'admisiones', label: 'Admisiones', icon: ClipboardCheck },
  { id: 'contenido', label: 'Contenido Web', icon: Image },
  { id: 'media', label: 'Archivos Multimedia', icon: FolderOpen },
  { id: 'cupones', label: 'Cupones', icon: Ticket },
  { id: 'opiniones', label: 'Opiniones', icon: MessageSquareQuote },
  { id: 'plantillas', label: 'Plantillas', icon: FileText },
  { id: 'seo', label: 'SEO Local', icon: MapPin },
  { id: 'listados', label: 'Listados de Valores', icon: ListChecks },
  { id: 'franquicias', label: 'Franquicias', icon: Building2 },
  { id: 'usuarios', label: 'Usuarios', icon: Users },
  { id: 'legal', label: 'Área Legal', icon: Scale },
  { id: 'consentimientos', label: 'Consentimientos', icon: ShieldCheck },
  { id: 'mantenimiento', label: 'Mantenimiento', icon: Wrench },
  { id: 'seguridad', label: 'Seguridad', icon: KeyRound },
  { id: 'integraciones', label: 'Integraciones', icon: Plug },
  { id: 'scme', label: 'Ecosistema SCME', icon: Boxes },
];

function Recaptcha({ siteKey, onToken }) {
  return <RecaptchaV2 siteKey={siteKey} onToken={onToken} />;
}

export default function Admin() {
  const [authed, setAuthed] = useState(() => !!getToken());
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rcConfig, setRcConfig] = useState(null);
  const [rcToken, setRcToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [section, setSection] = useState('dashboard');

  useEffect(() => {
    api.getAuthConfig().then(setRcConfig).catch(() => setRcConfig({ recaptchaEnabled: false }));
    const onLogout = () => { setAuthed(false); toast.error('Sesión finalizada. Vuelve a iniciar sesión.'); };
    window.addEventListener('natur-logout', onLogout);
    return () => window.removeEventListener('natur-logout', onLogout);
  }, []);

  useEffect(() => {
    if (authed) api.getMe().catch(() => {});
  }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let token = rcToken;
      if (rcConfig?.recaptchaEnabled && rcConfig.version === 'v3') {
        token = await executeV3(rcConfig.siteKey, 'login');
      } else if (rcConfig?.recaptchaEnabled && !token) {
        toast.error('Marca "No soy un robot" para continuar');
        setBusy(false);
        return;
      }
      const { token: jwt, user } = await api.login(email, password, token);
      setToken(jwt);
      setAuthed(true);
      setPassword('');
      toast.success(`Bienvenido al Panel MATRIZ, ${user.name}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, 'Error al iniciar sesión'));
      if (rcConfig?.recaptchaEnabled && rcConfig.version !== 'v3' && window.grecaptcha) { window.grecaptcha.reset(); setRcToken(''); }
    } finally { setBusy(false); }
  };

  const logout = () => {
    clearToken();
    clearElevated();
    setAuthed(false);
    setPassword('');
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center px-4">
        <form onSubmit={login} className="bg-white rounded-xl shadow-lg p-8 w-full max-w-sm text-center" data-testid="admin-login-form">
          <img src={LOGO_URL} alt="Natur Autoescuela" className="h-20 w-auto mx-auto mb-6" />
          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-5 h-5 text-amber-600" />
          </div>
          <h1 className="text-xl font-black text-stone-800 mb-1">Panel MATRIZ</h1>
          <p className="text-sm text-stone-500 mb-6">Inicia sesión para gestionar la tienda.</p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            autoComplete="username"
            className="w-full border border-stone-300 rounded px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 mb-3"
            data-testid="admin-email-input"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Contraseña"
            autoComplete="current-password"
            className="w-full border border-stone-300 rounded px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 mb-4"
            data-testid="admin-password-input"
          />
          {rcConfig?.recaptchaEnabled && rcConfig.version !== 'v3' && <Recaptcha siteKey={rcConfig.siteKey} onToken={setRcToken} />}
          <button type="submit" disabled={busy} className="btn-natur w-full py-3 rounded font-bold text-sm disabled:opacity-60" data-testid="admin-login-button">
            {busy ? 'Entrando…' : 'Entrar'}
          </button>
          {rcConfig?.recaptchaEnabled && <p className="text-[11px] text-stone-400 mt-3">Protegido por Google reCAPTCHA {rcConfig.version}</p>}
          <Link to="/" className="block text-xs text-teal-600 mt-4 hover:underline">← Volver a la tienda</Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 flex">
      {/* Sidebar */}
      <aside className="w-60 bg-stone-900 text-stone-300 flex flex-col shrink-0">
        <Link to="/" className="p-5 border-b border-stone-800 block" data-testid="admin-logo-link">
          <img src={LOGO_URL} alt="Natur Autoescuela" className="h-14 w-auto" />
          <p className="text-xs text-stone-500 mt-2 tracking-wide uppercase">Panel MATRIZ</p>
        </Link>
        <nav className="flex-1 py-4 overflow-y-auto">
          {SECTIONS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setSection(id)}
              className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${section === id ? 'bg-amber-500 text-white font-bold' : 'hover:bg-stone-800 hover:text-white'}`}
              data-testid={`admin-nav-${id}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-stone-800 space-y-1">
          <Link to="/" className="flex items-center gap-3 px-2 py-2 text-sm hover:text-white transition-colors" data-testid="admin-view-store">
            <Store className="w-4 h-4" /> Ver tienda
          </Link>
          <button onClick={logout} className="w-full flex items-center gap-3 px-2 py-2 text-sm hover:text-white transition-colors" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Salir
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 p-6 md:p-8 overflow-x-auto">
        {section === 'dashboard' && <AdminDashboard onNavigate={setSection} />}
        {section === 'productos' && <AdminProductos />}
        {section === 'pedidos' && <AdminPedidos />}
        {section === 'admisiones' && <AdminAdmisiones />}
        {section === 'contenido' && <AdminContenido />}
        {section === 'media' && <AdminMedia />}
        {section === 'cupones' && <AdminCupones />}
        {section === 'opiniones' && <AdminOpiniones />}
        {section === 'plantillas' && <AdminPlantillas />}
        {section === 'seo' && <AdminSeoLocal />}
        {section === 'listados' && <AdminListados />}
        {section === 'legal' && <AdminLegal />}
        {section === 'consentimientos' && <AdminConsentimientos />}
        {section === 'mantenimiento' && <AdminMantenimiento />}
        {section === 'seguridad' && <AdminSeguridad />}
        {['franquicias', 'usuarios', 'integraciones', 'scme'].includes(section) && <AdminSistema section={section} />}
      </main>
      <PinModal />
    </div>
  );
}
